import { supabase } from "./supabase";

const BUCKET = "plant-photos";

/**
 * Upload a File picked from an <input type="file"> to Supabase Storage
 * and return its public URL. Web equivalent of src/lib/storage.ts's
 * `uploadPlantImage` — same bucket, same object naming convention, just
 * reading from a Blob instead of RNFS.
 */
export async function uploadPlantImage(file: File): Promise<string> {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id || "anon";

  const lower = file.name.toLowerCase();
  const ext = lower.endsWith(".png") ? "png" : "jpg";
  const contentType = ext === "png" ? "image/png" : "image/jpeg";
  const objectName = `${userId}/${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(objectName, file, { contentType, upsert: false });
  if (uploadError) throw uploadError;

  const { data: pub } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(objectName);
  return pub.publicUrl;
}

export function isRemoteImageUrl(
  url: string | null | undefined
): boolean {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
}

/**
 * Read a File as base64 (without the data: prefix). Needed by the
 * Anthropic vision API which accepts base64-encoded images.
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // FileReader result is `data:<mime>;base64,<data>` — strip the prefix.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
